import type { ApolloError } from '@apollo/client';
import { useEffect, useRef, type FunctionComponent } from 'react';

import type { EventContainer } from '@trello/analytics-types';
import { formatContainers } from '@trello/atlassian-analytics';
import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { avatarsFromAvatarUrl } from '@trello/business-logic/member';
import { ErrorBoundary } from '@trello/error-boundaries';
import type { SentryErrorMetadata } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { Spinner } from '@trello/nachos/spinner';
import {
  dangerouslyConvertPrivacyString,
  type PIIString,
} from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import {
  getMemberActivityUrl,
  getMemberProfileUrl,
  isMeAlias,
} from '@trello/urls';

import { LazyError } from 'app/src/components/Error/LazyError';
import { RuntimeError } from 'app/src/components/Error/RuntimeError';
import { MemberHeaderParent } from 'app/src/components/MemberHome/MemberHeaderParent';
import { SettingsFullPage } from 'app/src/components/SettingsFullPage';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import { MemberProfileView } from './MemberProfile/MemberProfileView';
import {
  useMemberPageQuery,
  type MemberPageQuery,
} from './MemberPageQuery.generated';
import { useMemberPageViewEvents } from './useMemberPageViewEvents';

interface MemberProfilePageProps {
  username: PIIString;
}

interface MemberProfileInnerPageProps {
  member: NonNullable<MemberPageQuery['member']>;
  username: PIIString;
  taskAborted: ({
    containers,
    error,
  }: {
    containers?: EventContainer;
    error?: Error;
  }) => Promise<void>;
  taskSucceeded: ({ containers }: { containers?: EventContainer }) => void;
  loading: boolean;
  error: ApolloError | undefined;
}

const errorBoundaryTags: SentryErrorMetadata['tags'] = {
  ownershipArea: 'trello-billing-platform',
  feature: 'Members Profile Page',
};

export const updateUrl = (oldUsername?: PIIString, newUsername?: PIIString) => {
  if (!oldUsername || !newUsername) {
    return;
  }

  const newPath = location.pathname.replace(
    dangerouslyConvertPrivacyString(oldUsername),
    dangerouslyConvertPrivacyString(newUsername),
  );
  history.replaceState(null, '', newPath);
};

const MemberProfileInnerPage: FunctionComponent<
  MemberProfileInnerPageProps
> = ({ member, username, taskAborted, taskSucceeded, loading, error }) => {
  const loggedInMember = isMemberLoggedIn();
  // a ref is used here because we make a copy of member.username for comparison only once,
  // to check if the user is an anonymous member or the logged in user.
  const usernameRef = useRef(member.username);

  const { value: isBillplatWorkspaceSettingsEnabled } = useFeatureGate(
    'billplat_workspace_settings_navigation',
  );

  useEffect(() => {
    if (!member.organizations.length) {
      return;
    }
    /*
      This conditional is to cover the case for when the username passed in through the URL
      does not match the loggedInUser. In this case, redirect to the URL username with an anonymous view
    */
    if (
      !loggedInMember ||
      (loggedInMember && usernameRef.current !== username)
    ) {
      taskAborted({
        containers: formatContainers({
          idOrganization: member.organizations[0].id,
        }),
        error: new Error('Redirected away from member profile page'),
      });

      navigate(getMemberActivityUrl(username), {
        trigger: true,
        replace: true,
      });
      return;
    } else {
      taskSucceeded({
        containers: formatContainers({
          idOrganization: member.organizations[0].id,
        }),
      });
    }
  }, [
    taskSucceeded,
    taskAborted,
    loggedInMember,
    username,
    member?.organizations,
  ]);

  const avatars = member.avatarUrl
    ? avatarsFromAvatarUrl(member.avatarUrl)
    : undefined;

  if (isBillplatWorkspaceSettingsEnabled) {
    return (
      <ErrorBoundary
        tags={errorBoundaryTags}
        errorHandlerComponent={RuntimeError}
      >
        <SettingsFullPage>
          {loading ? (
            <WorkspacePageSpinner />
          ) : error ? (
            <LazyError errorType={error ? 'serverError' : 'notFound'} />
          ) : (
            <MemberProfileView />
          )}
        </SettingsFullPage>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      tags={errorBoundaryTags}
      errorHandlerComponent={RuntimeError}
    >
      <MemberHeaderParent
        id={member.id}
        name={member.fullName as unknown as PIIString}
        username={member.username}
        initials={member.initials as unknown as PIIString}
        avatars={avatars}
        isMemberMe={true}
        selected={'Profile'}
      />
      <MemberProfileView />
    </ErrorBoundary>
  );
};

export const MemberProfilePage: FunctionComponent<MemberProfilePageProps> = ({
  username,
}) => {
  const { startTask, taskSucceeded, taskAborted } = useMemberPageViewEvents({
    taskName: 'view-member/profile',
    source: 'memberProfileScreen',
  });

  useEffect(() => startTask(), [startTask]);

  const memberId = useMemberId();
  const { data, loading, error, previousData } = useMemberPageQuery({
    waitOn: ['MemberHeader'],
    variables: { memberId },
  });

  // sync the username in the URL with the username in the member state
  useEffect(() => {
    updateUrl(previousData?.member?.username, data?.member?.username);
  }, [data?.member?.username, previousData?.member?.username]);

  if (loading) {
    return <Spinner centered />;
  }

  if (error || !data?.member) {
    return <LazyError errorType={error ? 'serverError' : 'notFound'} />;
  }

  // Redirect to profile page with the username in the URL if using me alias or memberIds
  if (
    isMeAlias(dangerouslyConvertPrivacyString(username)) ||
    memberId === dangerouslyConvertPrivacyString(username)
  ) {
    navigate(getMemberProfileUrl(data?.member?.username), {
      trigger: true,
      replace: true,
    });
    return;
  }

  return (
    <MemberProfileInnerPage
      member={data.member}
      username={username}
      taskAborted={taskAborted}
      taskSucceeded={taskSucceeded}
      loading={loading}
      error={error}
    />
  );
};
